import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, ButtonBase, Skeleton, Typography } from '@mui/material';
import { addDays, format } from 'date-fns';
import { TaskActions } from '../actions/useTaskActions';
import { isHiddenScheduleClient } from '../../coachSchedules/hiddenClients';
import { withoutHidden } from '../inbox/inboxModel';
import { countableSessions, dayKey, formatClock, ScheduledSession, sessionsForDay } from '../schedule';
import { ClientTasksResult } from '../tasks/useClientTasks';
import { ClientWork, DashboardClient } from '../tasks/types';
import { INK, panelSx, SourceNotice, TONES } from '../ui';
import { daySource, DaySource, freshness, scheduleClientIds, scheduleNextSlots, sessionsFromSchedule, waitingNextLabel } from './scheduleSource';
import { attentionCount, headline, needingAttention, nextBookedSession, notOnSchedule } from './todayModel';
import { TodaySchedule } from './TodaySchedule';
import { useCoachSchedule, useScheduleClients } from './useCoachSchedule';
import { useCollaboratorClients } from './useCollaboratorClients';
import { WaitingRail } from './WaitingRail';

/** Re-renders once a minute so the "now" line and past sessions keep up. */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

const switchSx = (on: boolean) =>
  ({
    height: 32,
    px: 1.5,
    gap: 0.75,
    borderRadius: '6px',
    fontSize: 13,
    fontWeight: on ? 600 : 500,
    color: on ? INK.main : INK.body,
    bgcolor: on ? '#FFFFFF' : 'transparent',
    boxShadow: on ? '0 1px 2px rgba(15,23,42,0.12)' : 'none',
  }) as const;

/** Why the day isn't coming from Mindbody, when the coach expected it to. */
const ScheduleNotice: React.FC<{ source: DaySource }> = ({ source }) => {
  if (source.kind !== 'fallback' || source.reason === 'off') return null;
  return source.reason === 'not-yet' ? (
    <Alert severity="info">Tomorrow's schedule arrives at 5:00 AM. Until then this shows bookings from the client list, which can be a day old.</Alert>
  ) : (
    <Alert severity="warning">
      Mindbody sessions didn't load, so this shows bookings from the client list. They can be a day old and miss some sessions.
    </Alert>
  );
};

/**
 * The coach's landing view: today's (or tomorrow's) sessions, each carrying
 * what that client needs, and beside it everyone else who is waiting.
 * Unless the account opts out (`mindbody_schedule` false), the sessions come from the coach's
 * Mindbody schedule (synced 05:00 and 12:00), falling back to the client list.
 */
export const TodayView: React.FC<{
  tasks: ClientTasksResult;
  actions: TaskActions;
  coachId: string;
  /** Shown after the date for team scopes; a coach's own view needs no label. */
  scopeLabel: string | null;
  /** Load clients the coach collaborates on; a team scope that reads every client already has them. */
  includeCollaborators: boolean;
  /** Read the day from the coach's Mindbody schedule (on unless the account opts out). */
  mindbodySchedule?: boolean;
  /** Signed-in email, for the hidden test client. */
  viewerEmail?: string | null;
  onOpenInInbox: (clientId?: string) => void;
}> = ({ tasks, actions, coachId, scopeLabel, includeCollaborators, mindbodySchedule = false, viewerEmail = null, onOpenInInbox }) => {
  const now = useMinuteClock();
  const [day, setDay] = useState<'today' | 'tomorrow'>('today');
  const [show, setShow] = useState<'all' | 'attention'>('all');
  const tomorrowDate = useMemo(() => addDays(now, 1), [now]);
  const date = day === 'today' ? now : tomorrowDate;
  const checking = Object.values(tasks.sources).some((s) => s === 'loading');
  const collaborators = useCollaboratorClients(coachId, includeCollaborators);
  const live = useCoachSchedule(coachId, mindbodySchedule, dayKey(now), dayKey(tomorrowDate));
  const todaySource = useMemo(() => daySource(live, dayKey(now), now), [live, now]);
  const tomorrowSource = useMemo(() => daySource(live, dayKey(tomorrowDate), now), [live, tomorrowDate, now]);
  const source = day === 'today' ? todaySource : tomorrowSource;

  // With the schedule, the test client is hidden everywhere on Today, not only in it; opted out, Today is as before.
  const hidden = useMemo(
    () => (clientId: string) => mindbodySchedule && isHiddenScheduleClient(clientId, viewerEmail),
    [mindbodySchedule, viewerEmail]
  );
  const listed = useMemo(() => {
    const known = new Set(tasks.clients.map((client) => client.docId));
    return [...tasks.clients, ...collaborators.clients.filter((client: DashboardClient) => !known.has(client.docId))].filter(
      (client) => !hidden(client.clientId)
    );
  }, [tasks.clients, collaborators.clients, hidden]);
  const taskClientIds = useMemo(() => new Set(tasks.clients.map((client) => client.clientId)), [tasks.clients]);
  // Scheduled clients the coach's lists don't hold (led by someone else, or not in the mirror) are looked up by id.
  const unlisted = useMemo(() => {
    const known = new Set(listed.map((client) => client.clientId));
    const schedules = [todaySource, tomorrowSource].map((s) => (s.kind === 'schedule' ? s.schedule : null));
    return scheduleClientIds(schedules, viewerEmail).filter((id) => !known.has(id));
  }, [listed, todaySource, tomorrowSource, viewerEmail]);
  const listsSettled = tasks.status !== 'loading' && collaborators.status !== 'loading';
  const looked = useScheduleClients(unlisted, listsSettled);
  const clients = useMemo(() => {
    const known = new Set(listed.map((client) => client.docId));
    return [...listed, ...looked.clients.filter((client) => !known.has(client.docId))];
  }, [listed, looked.clients]);
  const clientsById = useMemo(() => new Map(clients.map((client) => [client.clientId, client])), [clients]);

  const work = useMemo(
    () => withoutHidden(tasks.work, actions.hiddenTaskIds).filter((item) => !hidden(item.client.clientId)),
    [tasks.work, actions.hiddenTaskIds, hidden]
  );
  const workByClient = useMemo(() => new Map<string, ClientWork>(work.map((item) => [item.client.clientId, item])), [work]);
  const sessionsFrom = (from: DaySource, target: Date): ScheduledSession[] => {
    if (from.kind === 'loading') return [];
    if (from.kind === 'fallback') return sessionsForDay(clients, workByClient, coachId, target, now);
    return from.schedule ? sessionsFromSchedule(from.schedule, clientsById, workByClient, coachId, now, viewerEmail) : [];
  };
  const todaySessions = useMemo(() => sessionsFrom(todaySource, now), [todaySource, clients, clientsById, workByClient, coachId, now, viewerEmail]); // eslint-disable-line react-hooks/exhaustive-deps
  const tomorrowSessions = useMemo(() => sessionsFrom(tomorrowSource, tomorrowDate), [tomorrowSource, tomorrowDate, clients, clientsById, workByClient, coachId, now, viewerEmail]); // eslint-disable-line react-hooks/exhaustive-deps
  const sessions = day === 'today' ? todaySessions : tomorrowSessions;
  const booked = countableSessions(sessions);
  const tomorrowBooked = countableSessions(tomorrowSessions);
  const waiting = useMemo(() => notOnSchedule(work, sessions), [work, sessions]);
  // The waiting rail's "Next session": the schedule's times where it covers the day, the client list's beyond.
  const nextLabel = useMemo(() => {
    const scheduledDates = new Set<string>();
    if (todaySource.kind === 'schedule') scheduledDates.add(dayKey(now));
    if (tomorrowSource.kind === 'schedule') scheduledDates.add(dayKey(tomorrowDate));
    if (scheduledDates.size === 0) return undefined;
    const slots = scheduleNextSlots(todaySource.kind === 'schedule' ? todaySessions : null, tomorrowSource.kind === 'schedule' ? tomorrowSessions : null);
    return (client: DashboardClient) => waitingNextLabel(client, coachId, now, slots, scheduledDates);
  }, [todaySource, tomorrowSource, todaySessions, tomorrowSessions, coachId, now, tomorrowDate]);
  const attention = attentionCount(sessions);
  // Nothing to filter to once nobody needs attention (or the last one is cleared): fall back to everyone.
  const noneNeedAttention = !checking && attention === 0;
  const filter = noneNeedAttention ? 'all' : show;
  const shown = filter === 'attention' ? needingAttention(sessions) : sessions;
  // On Today, tomorrow's schedule answers "when next" first, so the line can't contradict the preview below it.
  const next =
    booked.length > 0
      ? null
      : day === 'today' && tomorrowSource.kind === 'schedule'
        ? tomorrowBooked.length > 0
          ? { name: tomorrowBooked[0].clientName, label: `${format(tomorrowDate, 'EEEE d MMM')} at ${formatClock(tomorrowBooked[0].appointment.time)}` }
          : nextBookedSession(clients, coachId, tomorrowDate)
        : nextBookedSession(clients, coachId, date);
  const fresh = source.kind === 'schedule' ? freshness(source.syncedAt, now) : null;
  // A collaborator who appears in the Mindbody schedule is looked up by id, so the list failing loses nothing there.
  const collaboratorsMissing = collaborators.status === 'error' && source.kind === 'fallback';

  if (tasks.status === 'error') {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={tasks.refresh}>Retry</Button>}>
        {tasks.error}
      </Alert>
    );
  }
  const loadingClients =
    (tasks.status === 'loading' && tasks.clients.length === 0) ||
    source.kind === 'loading' ||
    (source.kind === 'schedule' && collaborators.status === 'loading');

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '20px 32px', mb: 3 }}>
        <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: INK.muted, mb: 1 }}>
            {[format(date, 'EEEE d MMMM'), scopeLabel].filter(Boolean).join(' · ')}
            {fresh ? (
              <Box component="span" sx={{ color: fresh.late ? TONES.soon.color : INK.muted }}>
                {` · ${fresh.label}`}
                {fresh.late ? `. ${fresh.late}` : ''}
              </Box>
            ) : null}
          </Typography>
          {loadingClients ? (
            <Skeleton width="40%" height={40} />
          ) : (
            <Typography component="h1" sx={{ fontSize: { xs: 22, md: 28 }, lineHeight: 1.25, fontWeight: 600, letterSpacing: '-0.02em' }}>
              {headline(sessions, day)}
            </Typography>
          )}
          {!loadingClients && booked.length > 0 ? (
            <Box role="group" aria-label="Show" sx={{ display: 'inline-flex', mt: 1.5, p: '3px', gap: '3px', bgcolor: '#E7EBF0', borderRadius: '9px' }}>
              <ButtonBase aria-pressed={filter === 'all'} onClick={() => setShow('all')} sx={switchSx(filter === 'all')}>
                All
                <Box component="span" sx={{ color: INK.muted }}>
                  {booked.length}
                </Box>
              </ButtonBase>
              <ButtonBase
                aria-pressed={filter === 'attention'}
                disabled={noneNeedAttention}
                onClick={() => setShow('attention')}
                sx={{ ...switchSx(filter === 'attention'), '&.Mui-disabled': { color: INK.muted, cursor: 'default' } }}
              >
                <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: attention > 0 ? '#E8A317' : '#94A3B8' }} />
                Needs attention
                <Box component="span" sx={{ color: attention > 0 ? '#8A5100' : INK.muted }}>
                  {checking ? '…' : attention}
                </Box>
              </ButtonBase>
            </Box>
          ) : null}
        </Box>
        <Box role="group" aria-label="Day" sx={{ display: 'flex', p: 0.5, gap: 0.5, bgcolor: '#E7EBF0', borderRadius: '10px' }}>
          {(['today', 'tomorrow'] as const).map((option) => {
            const on = option === day;
            return (
              <ButtonBase
                key={option}
                aria-pressed={on}
                onClick={() => setDay(option)}
                sx={{ height: 36, px: 1.75, borderRadius: '7px', fontSize: 14, fontWeight: 500, bgcolor: on ? '#FFFFFF' : 'transparent', boxShadow: on ? '0 1px 2px rgba(15,23,42,0.12)' : 'none' }}
              >
                {option === 'today' ? 'Today' : 'Tomorrow'}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <ScheduleNotice source={source} />
        <SourceNotice sources={tasks.sources} failures={tasks.failures} onRetry={tasks.refresh} />
        {collaboratorsMissing ? (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={collaborators.retry}>Retry</Button>}>
            Sessions with clients you collaborate on didn't load, so some sessions may be missing.
          </Alert>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '40px 48px', alignItems: 'flex-start' }}>
        <Box component="section" aria-label="Schedule" sx={{ flex: '999 1 560px', minWidth: 0 }}>
          {loadingClients ? (
            [0, 1, 2].map((i) => <Skeleton key={i} height={72} sx={{ mb: 1 }} />)
          ) : booked.length === 0 ? (
            <>
              <Box sx={{ ...panelSx, p: 3, maxWidth: 600, mb: sessions.length > 0 ? 3 : 0 }}>
                <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                  Nothing booked {day}
                </Typography>
                <Typography sx={{ color: INK.body, mb: 2 }}>
                  {next ? `Your next session is ${next.label} with ${next.name}.` : 'You have no upcoming sessions booked.'}{' '}
                  {waiting.length > 0 ? `${waiting[0].client.name} is the most overdue on your list.` : ''}
                </Typography>
                {waiting.length > 0 ? (
                  <Button variant="contained" disableElevation onClick={() => onOpenInInbox(waiting[0].client.clientId)}>
                    Open {waiting[0].client.name.split(' ')[0]}'s tasks
                  </Button>
                ) : null}
              </Box>
              {/* Only late cancels and no-shows are left: listed muted, so the day still accounts for them. */}
              {sessions.length > 0 ? (
                <TodaySchedule sessions={sessions} actions={actions} now={now} showNow={false} checking={checking} resolvingClients={!listsSettled || looked.pending || looked.failed}
                  taskClientIds={taskClientIds}
                />
              ) : null}
            </>
          ) : shown.length === 0 ? (
            <Box sx={{ ...panelSx, p: 3, maxWidth: 600 }}>
              <Typography sx={{ color: INK.body, mb: 1.5 }}>No session {day} needs attention.</Typography>
              <Button onClick={() => setShow('all')} sx={{ textTransform: 'none', fontWeight: 600, ml: -1 }}>
                Show all {booked.length} sessions
              </Button>
            </Box>
          ) : (
            <TodaySchedule
              sessions={shown}
              actions={actions}
              now={now}
              showNow={day === 'today'}
              checking={checking}
              resolvingClients={!listsSettled || looked.pending || looked.failed}
              taskClientIds={taskClientIds}
            />
          )}
          {day === 'today' && tomorrowBooked.length > 0 && !loadingClients ? (
            <Button onClick={() => setDay('tomorrow')} sx={{ textTransform: 'none', color: INK.body, ml: { sm: '92px' }, fontWeight: 400 }}>
              Tomorrow: {tomorrowBooked.length} session{tomorrowBooked.length === 1 ? '' : 's'}, first at {formatClock(tomorrowBooked[0].appointment.time)} with{' '}
              {tomorrowBooked[0].clientName}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600, ml: 1 }}>
                View →
              </Box>
            </Button>
          ) : null}
        </Box>
        <Box component="aside" sx={{ flex: '1 1 340px', minWidth: 0 }}>
          <WaitingRail
            work={waiting}
            loading={loadingClients}
            now={now}
            dayLabel={day}
            nextLabel={nextLabel}
            onOpen={(clientId) => onOpenInInbox(clientId)}
            onSeeAll={() => onOpenInInbox()}
          />
        </Box>
      </Box>
    </Box>
  );
};
